import { Model } from 'pinia-orm';
import { useCommonStore } from "../../shared/state/common";
import Stage from './Stage';
import Subscription from './Subscription';
import Domain from './Domain';
import Attachment from './Attachment';
import Filter from './Filter';
import JobFilter from './JobFilter';
import VdabJob from './VdabJob';
import moment from 'moment';
import Company from './Company';
import EmailTemplate from './EmailTemplate';
import JobInternalNoteAttachment from './JobInternalNoteAttachment';
import Location from './Location';
import Campaign from './Campaign';
import User from './User';
import JobChannel from './JobChannel';
import JobJobChannel from './JobJobChannel';
import Tag from './Tag';
import Taggable from './Taggable';
import InterimJob from './InterimJob';
import { trim } from 'lodash';

export default class Job extends Model {

    static entity = 'jobs';

    static fields() {
        return {
            _published: this.attr(false),
            id: this.uid(),
            archived: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            last_published_at: this.attr(null),
            start_date: this.attr(null),
            end_date: this.attr(null),
            image_url: this.attr(''),
            og_image_url: this.attr(''),
            postal_code: this.attr(''),
            city: this.attr(''),
            country: this.attr(''),
            company_location_id: this.attr(null),
            video_url: this.attr(''),
            reference_number: this.attr(''),
            published: this.attr(false),
            schedule: this.attr(false),
            is_spontaneous: this.attr(false),
            sequence: this.attr(0),
            dynamic_form: this.attr({}),
            dynamic_form_interim: this.attr({}),
            // translatable attributes
            url: this.attr({}),
            active: this.attr({}),
            description: this.attr({}),
            description_title: this.attr({}),
            offer: this.attr({}),
            offer_title: this.attr({}),
            og_description: this.attr({}),
            og_title: this.attr({}),
            profile: this.attr({}),
            profile_title: this.attr({}),
            short_description: this.attr({}),
            slug: this.attr({}),
            thank_you_scripts: this.attr({}),
            title: this.attr({}),
            extra: this.attr({}),
            use_auto_pdf: this.attr({}),
            title_pdf: this.attr({}),
            text_pdf: this.attr({}),
            logo_in_pdf: this.attr({}),
            button_in_pdf: this.attr({}),
            timing: this.attr(null),
            shift_hours: this.attr(null),
            contract_type: this.attr(null),
            type: this.attr(0),
            internal_notes: this.attr(null),
            status: this.attr(null),
            wanted_experience: this.attr(null),
            total_applications_count: this.attr(null),
            rejected_applications_count: this.attr(null),
            completed_applications_count: this.attr(null),
            domain_id: this.attr(null),
            company_id: this.attr(null),
            is_demo_data: this.attr(false),
            previewHash: this.string(''),
            confirmation_template_id: this.attr(null),
            active_location: this.attr({}),
            remote: this.attr(0),
            responsible_user_id: this.attr(null),
            competence_template_id: this.attr(false),
            interview_template_id: this.attr(false),
            request_reason_ids: this.attr([]),
            reject_reason_ids: this.attr([]),
            new_applications_count: this.attr(0),
            active_campaigns_count: this.attr(0),
            place_of_work: this.attr(null),

            days_since_creation: this.attr(0),
            days_since_publishing: this.attr(0),

            //relationships
            stages: this.hasMany(Stage, 'job_id'),
            subscriptions: this.morphMany(
                Subscription,
                'subable_id',
                'subable_type'
            ),
            domain: this.belongsTo(Domain, 'domain_id'),
            attachments: this.hasMany(Attachment, 'job_id'),
            internalNoteAttachments: this.hasMany(
                JobInternalNoteAttachment,
                'job_id'
            ),
            filters: this.belongsToMany(
                Filter,
                JobFilter,
                'job_id',
                'filter_id'
            ),
            tags: this.morphToMany(
                Tag,
                Taggable,
                'tag_id',
                'taggable_id',
                'taggable_type'
            ),
            company: this.belongsTo(Company, 'company_id'),
            vdabJob: this.hasOne(VdabJob, 'job_id'),
            location: this.belongsTo(Location, 'company_location_id'),
            confirmation_template: this.belongsTo(
                EmailTemplate,
                'confirmation_template_id'
            ),
            campaigns: this.hasMany(Campaign, 'job_id'),
            responsible_user: this.belongsTo(User, 'responsible_user_id'),
            jobChannels: this.belongsToMany(
                JobChannel,
                JobJobChannel,
                'job_id',
                'job_channel_id'
            ),
            booster: this.attr(null),
            interimJobs: this.hasMany(InterimJob, 'job_id'),
        };
    }

    get is_published() {
        let published = !this.published
            ? false
            : !this.schedule
                ? true
                : (this.start_date === null ||
                    moment().utc().isAfter(this.start_date)) &&
                (this.end_date === null || moment(this.end_date).isAfter());

        if (
            this.schedule &&
            this.end_date &&
            !moment().isBefore(moment(this.end_date))
        ) {
            published = false;
        }

        return published && Object.values(this.active).some((val) => val);
    }

    get startMoment() {
        return moment(this.start_date);
    }

    get endMoment() {
        return moment(this.end_date);
    }

    get jobTypes() {
        return Array.isArray(this.type) ? this.type.slice().sort() : [];
    }

    get titleInAnyLanguage() {
        const commonStore = useCommonStore();
        const myLang = commonStore.locale;

        if (this.title[myLang]) return this.title[myLang];

        for (
            let i = 0;
            i < commonStore.availableLanguages.length;
            i++
        ) {
            const locale = commonStore.availableLanguages[i].value;

            if (this.title[locale]) return this.title[locale];
        }

        return this.$t('jobs.missing_title');
    }

    get countryCode() {
        if (
            this.active_location &&
            this.active_location.country &&
            trim(this.active_location.country).length > 0
        ) {
            return this.active_location.country;
        }

        if (this.country && trim(this.country).length > 0) {
            return this.country;
        }

        return '';
    }

    get active_languages() {
        return Object.keys(this.active).filter(key => !!this.active[key]);
    }
}
